import $api from "@/zapi-frontend/shared/api/axios";
import { CACHE_TIME } from "@/zapi-frontend/shared/const/consts";
import { TDocument } from "@/zapi-frontend/shared/hooks/useDocuments";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

export enum TUsefulnessStatus {
  USEFUL = "USEFUL",
  USELESS = "USELESS"
}

export type TDocumentUsefulness = {
  id: number;
  usefulness: TUsefulnessStatus;
};

export type TDocumentWithUsefulness = TDocument & {
  usefulness: TUsefulnessStatus;
  contentType?: "FULL" | "SUMMARIZED";
};

const getDocumentsUsefulness = async (projectId: string, chapterId: string) => {
  const response = await $api.get<{ data: TDocumentUsefulness[] }>(
    `/projects/${projectId}/chapters/${chapterId}/documents-usefulness`
  );
  return response.data.data;
};

function patchDocumentUsefulness({
  projectId,
  chapterId,
  documentId,
  usefulness
}: {
  projectId: string;
  chapterId: string;
  documentId: number;
  usefulness: TUsefulnessStatus;
}) {
  return $api.patch(`/projects/${projectId}/chapters/${chapterId}/documents-usefulness`, {
    documentId,
    usefulness
  });
}

export function useDocumentsUsefulness(projectId: string, chapterId: string, documents: TDocument[], enabled = true) {
  const [documentsWithUsefulness, setDocumentsWithUsefulness] = useState<TDocumentWithUsefulness[]>([]);
  const [contentPreferences, setContentPreferences] = useState<Record<number, boolean>>({});
  const queryClient = useQueryClient();

  const { data, isFetching } = useQuery({
    queryKey: ["documents-usefulness", projectId, chapterId],
    queryFn: () => getDocumentsUsefulness(projectId, chapterId),
    enabled: enabled && !!projectId && !!chapterId,
    staleTime: CACHE_TIME,
    refetchOnWindowFocus: false
  });

  // May be a bad fix, but it was a hotfix
  const usefulnessMutation = useMutation({
    mutationFn: ({ documentId, usefulness }: { documentId: number; usefulness: TUsefulnessStatus }) =>
      patchDocumentUsefulness({ projectId, chapterId, documentId, usefulness }),
    onMutate: async ({ documentId, usefulness }) => {
      await queryClient.cancelQueries({ queryKey: ["documents-usefulness", projectId, chapterId] });

      const previousData = queryClient.getQueryData<TDocumentUsefulness[]>([
        "documents-usefulness",
        projectId,
        chapterId
      ]);

      queryClient.setQueryData<TDocumentUsefulness[]>(["documents-usefulness", projectId, chapterId], (old) => {
        if (!old) return old;
        const existingDoc = old.find((d) => d.id === documentId);
        if (existingDoc) {
          return old.map((d) => (d.id === documentId ? { ...d, usefulness } : d));
        } else {
          return [...old, { id: documentId, usefulness }];
        }
      });

      return { previousData };
    },
    onError: (err, _variables, context) => {
      console.error("Error updating usefulness", err);
      if (context?.previousData) {
        queryClient.setQueryData(["documents-usefulness", projectId, chapterId], context.previousData);
      }
    }
  });

  useEffect(() => {
    setDocumentsWithUsefulness((prev) =>
      prev.map((d) => ({
        ...d,
        usefulness:
          data?.find((u: TDocumentUsefulness) => u.id === d.id)?.usefulness || d.usefulness || TUsefulnessStatus.USEFUL
      }))
    );
  }, [data]);

  useEffect(() => {
    setDocumentsWithUsefulness((prev) =>
      documents.map((d) => ({
        ...d,
        usefulness: prev.find((u) => u.id === d.id)?.usefulness || TUsefulnessStatus.USEFUL,
        useSummarized: contentPreferences[d.id] ?? false
      }))
    );
  }, [documents, contentPreferences]);

  const updateUsefulness = useCallback(
    async (documentId: number, usefulness: TUsefulnessStatus) => {
      // Update local state optimistically
      setDocumentsWithUsefulness((prev) => prev.map((d) => (d.id === documentId ? { ...d, usefulness } : d)));

      // Trigger the mutation which will update the React Query cache
      await usefulnessMutation.mutateAsync({ documentId, usefulness });
    },
    [usefulnessMutation]
  );

  const toggleContentPreference = useCallback((documentId: number, useSummarized: boolean) => {
    setContentPreferences((prev) => ({
      ...prev,
      [documentId]: useSummarized
    }));

    setDocumentsWithUsefulness((prev) => prev.map((d) => (d.id === documentId ? { ...d, useSummarized } : d)));
  }, []);

  return {
    documentsWithUsefulness,
    isDocumentsUsefulnessLoading: isFetching,
    updateUsefulness,
    isUpdatingUsefulness: usefulnessMutation.isPending,
    toggleContentPreference
  };
}
